import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Barcode, LayoutDashboard, Tags, Map, ClipboardList, Layers, LogOut, Settings, ScanLine, Grid2X2, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/labels", label: "Labels", icon: Tags, id: "nav-labels" },
  { to: "/platform", label: "Platform", icon: Store, id: "nav-platform" },
  { to: "/scan", label: "Scan", icon: ScanLine, id: "nav-scan" },
  { to: "/picklist", label: "Picklist", icon: ClipboardList, id: "nav-picklist" },
  { to: "/mappings", label: "SKU Mapping", icon: Map, id: "nav-mappings" },
  { to: "/batches", label: "Batches", icon: Layers, id: "nav-batches" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings", admin: true },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV.filter((n) => !n.admin || user?.role === "admin");
  return (
    <div className="min-h-screen">
      <header className="no-print sticky top-0 z-50 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="nav-hub" title="Back to Operations hub" className="flex h-9 w-9 items-center justify-center rounded-sm border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white">
              <Grid2X2 className="h-4 w-4" />
            </Link>
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-blue-600 text-white">
              <Barcode className="h-5 w-5" />
            </div>
            <div className="leading-none">
              <div className="font-heading text-xl font-extrabold uppercase tracking-tight">SND Label Manager</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">StarAndDaisy Operations</div>
            </div>
          </div>
          <nav className="hidden items-center gap-1 md:flex">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/dashboard"} data-testid={n.id}
                className={({ isActive }) => cn("flex items-center gap-2 rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-200",
                  isActive ? "bg-blue-600/20 text-blue-300" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100")}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span data-testid="user-email" className="hidden font-mono text-xs text-slate-400 sm:inline">{user?.emp_id || user?.email} <span data-testid="user-role" className="ml-1 rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-slate-300">{user?.role}</span></span>
            <Button data-testid="user-logout-button" variant="outline" size="sm" className="border-slate-700 bg-transparent hover:bg-slate-800"
              onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="mr-1 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
        <nav className="flex gap-1 overflow-x-auto border-t border-slate-800 px-2 py-1 md:hidden">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/dashboard"} data-testid={`${n.id}-mobile`}
              className={({ isActive }) => cn("flex shrink-0 items-center gap-1 rounded-sm px-3 py-1.5 text-xs font-medium",
                isActive ? "bg-blue-600/20 text-blue-300" : "text-slate-400")}>
              <n.icon className="h-3.5 w-3.5" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
};
